import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import {
  applyFiltersHelper,
  calculatePageCount,
  setCurrentPageHelper,
  setPageSizeHelper
} from "../../../../utils/TableUtils";
import { SortDirection } from "../../../shared/entities/Sort";
import TableContext, { TableContextValues } from "../../TableContext";
import { TaskListRequest } from "../../entities/TaskListRequest";
import {
  ScheduledTask,
  ScheduledTaskListResponse
} from "../../entities/scheduledTask/ScheduledTaskList";
import { TaskListFilters } from "../../entities/TaskListFilters";
import CompanyService from "../../../../services/CompanyService";
import { ScheduledTaskService } from "../../../../services/ScheduledTaskService";
import { ScheduledTaskListRequest } from "../../entities/scheduledTask/ScheduledTaskListRequest";

let reqId = v4();

export class ScheduledTaskTableContextValues extends TableContextValues<
  ScheduledTaskListResponse,
  TaskListFilters
> {
  constructor(
    public request: TaskListRequest,
    public setRequest: (request: TaskListRequest) => void
  ) {
    super();
  }
}

type Cmp = "string" | "number";

type ViewState = {
  page: number;
  pagesize: number;
  sortColumn: string;
  sortDirection: SortDirection;
};

type AccessorValue = string | number | null | undefined;

/**
 * `RepeatPeriod`/`RepeatFrequency` -> a single sortable rank. Groups Daily
 * (0) < Weekly (1, by weekday) < Monthly (2, by day). Unknown period is
 * blank (matches the `""` default `getPeriod` renders in
 * ScheduleTaskTableItem.tsx).
 */
export function periodRank(task: ScheduledTask): number | null {
  const period = task.RepeatPeriod;
  if (period === 0 || period === 1 || period === 2) {
    return period * 100 + (task.RepeatFrequency ?? 0);
  }
  return null;
}

/**
 * Keyed by the value ACTUALLY RENDERED in that column (verified against
 * ScheduleTaskTableItem.tsx), not by what the header key name suggests:
 * `RememberDays` renders `dueDays`, `DueDays` renders the repeat-period text.
 */
export const SORT_ACCESSORS: Record<
  string,
  {
    kind: Cmp;
    get: (task: ScheduledTask, userMap: Map<string, string>) => AccessorValue;
  }
> = {
  Client: { kind: "string", get: (t) => t.Person?.Name },
  Responsible: {
    kind: "string",
    get: (t, userMap) => userMap.get(String(t.ResponsibleUserID ?? ""))
  },
  RememberDays: { kind: "number", get: (t) => t.dueDays },
  DueDays: { kind: "number", get: (t) => periodRank(t) },
  Description: { kind: "string", get: (t) => t.Description }
};

function isBlank(value: AccessorValue): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value))
  );
}

/**
 * Ascending: blanks first, then a numeric-aware `localeCompare` for strings
 * or plain subtraction for numbers. Descending negates the full ascending
 * result, so blanks land last.
 */
export function buildComparator(
  kind: Cmp,
  get: (task: ScheduledTask, userMap: Map<string, string>) => AccessorValue,
  userMap: Map<string, string>,
  direction: SortDirection
) {
  return (a: ScheduledTask, b: ScheduledTask): number => {
    const valueA = get(a, userMap);
    const valueB = get(b, userMap);
    const blankA = isBlank(valueA);
    const blankB = isBlank(valueB);

    let result: number;
    if (blankA && blankB) {
      result = 0;
    } else if (blankA) {
      result = -1;
    } else if (blankB) {
      result = 1;
    } else if (kind === "number") {
      result = (valueA as number) - (valueB as number);
    } else {
      result = String(valueA).localeCompare(String(valueB), undefined, {
        numeric: true,
        sensitivity: "base"
      });
    }

    return direction === SortDirection.Descending ? -result : result;
  };
}

const ScheduledTaskTableProvider = (
  props: React.PropsWithChildren<{
    userId: string | null | undefined;
    request?: ScheduledTaskListRequest;
  }>
) => {
  const [request, setRequest] = useState<ScheduledTaskListRequest>(
    props.request ??
      new ScheduledTaskListRequest(
        props.userId === undefined ? CompanyService.getUserid() : props.userId,
        CompanyService.getAdditionalDefinitions() ?? [],
        null
      )
  );

  const [viewState, setViewState] = useState<ViewState>({
    page: 0,
    pagesize: request.pagesize,
    sortColumn: "Client",
    sortDirection: SortDirection.Ascending
  });

  const [response, setResponse] = useState<ScheduledTaskListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const applySearch = (keyword: string) => {
    setRequest({ ...request, text: keyword });
  };

  const requestData = async () => {
    setLoading(true);
    reqId = v4();
    const ourRequest = reqId;
    const result = await ScheduledTaskService.getScheduledTaskList(request);
    if (ourRequest !== reqId) {
      return;
    }
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    setResponse(result);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, [request]);
  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  // Filter/search/person changes produce a new `request` reference and reset
  // the page; `reload()` (e.g. after editing a task) keeps the same
  // `request` reference, so it does NOT reset the page.
  useEffect(() => {
    setViewState((v) => ({ ...v, page: 0 }));
  }, [request]);

  const derived = useMemo(() => {
    if (!response) {
      return undefined;
    }
    const userMap = new Map<string, string>(
      (CompanyService.getUsers() ?? []).map((u) => [u.Id, u.Value])
    );
    const accessor = SORT_ACCESSORS[viewState.sortColumn];
    const sortedList = [...response.list];
    if (accessor) {
      sortedList.sort(
        buildComparator(
          accessor.kind,
          accessor.get,
          userMap,
          viewState.sortDirection
        )
      );
    }
    const itemCount = response.list.length;
    const pagesize = viewState.pagesize;
    const rawPageCount = pagesize > 0 ? Math.ceil(itemCount / pagesize) : 0;
    const effectivePage = Math.min(
      viewState.page,
      Math.max(0, rawPageCount - 1)
    );
    const list = sortedList.slice(
      effectivePage * pagesize,
      (effectivePage + 1) * pagesize
    );
    return {
      ...response,
      page: effectivePage,
      pageSize: pagesize,
      itemCount,
      list
    };
  }, [response, viewState]);

  const setSortColumn = (key: string) => {
    setViewState((v) => ({
      ...v,
      sortColumn: key,
      sortDirection:
        v.sortColumn === key
          ? (Number(!v.sortDirection) as SortDirection)
          : SortDirection.Descending,
      page: 0
    }));
  };

  const sort = {
    sortDirection: viewState.sortDirection,
    sortColumn: viewState.sortColumn
  };
  const pageCount = calculatePageCount(derived);
  const value = new ScheduledTaskTableContextValues(request, setRequest);
  value.loading = loading;
  value.error = error;
  value.response = derived;
  value.currentPage = derived?.page ?? viewState.page;
  value.setCurrentPage = setCurrentPageHelper(setViewState, pageCount);
  value.pageCount = pageCount;
  value.reload = requestData;
  value.applyFilters = applyFiltersHelper(setRequest);
  value.applySearch = applySearch;
  value.sort = sort;
  value.pageSize = viewState.pagesize;
  value.setSortColumn = setSortColumn;
  value.setPageSize = setPageSizeHelper(setViewState);

  return (
    <TableContext.Provider value={value}>
      {props.children}
    </TableContext.Provider>
  );
};

export default ScheduledTaskTableProvider;
